import { ReactNode, useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Card, Col, Row, Space, Tag, Typography } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { SH } from 'common/components';
import EIQDocumentPreview from 'common/components/document-preview/eiq-document-preview';
import InfoField from 'common/components/info-block/info-field/info-field';
import { DATE_SHORT_MMM_DD, DATE_SHORT_MMM_DD_YYYY, DATE_SHORT_MMM_D_YYYY_H_MMA } from 'common/constans/date.constants';
import { ownershipTypeStatusMap } from 'common/constans/common.constants';
import { ResidentTypesEnum } from 'common/enums/resident-types.enum';
import { RentalReservationDocumentTypeEnum, rentalReservationDocumentTypeMap } from 'common/enums/rental-reservation-document-type.enum';
import { rentalReservationFeeTypeColorMap } from 'common/enums/rental-reservation-fee-type.enum';
import { RentalPaymentStatusEnum, rentalPaymentStatusColorMap, rentalPaymentStatusMap } from 'common/enums/rental-payment-status.enum';
import { RentalReservationStatusEnum, rentalReservationStatusColorMap } from 'common/enums/rental-reservation-status.enum';
import { formatAddressLabel } from 'common/helpers/address.helper';
import { formatRentalPropertyAgencyLabel } from 'common/helpers/rental-property.helper';
import { formatRentalReservationNumber, formatRentalReservationReviewPeriod } from 'common/helpers/rental-reservation.helper';
import { useModal } from 'common/hooks/use-modal/use-modal.hook';
import { UploadFileTypes } from 'common/services/file-service/file.service.types';
import rentalReservationService from 'common/services/rental-reservation-service/rental-reservation.service';
import {
  IRentalReservationOccupantRead,
  IRentalReservationPetRead,
  IRentalReservationVehicleRead,
  IRentalReservationDetailsRead,
} from 'common/services/rental-reservation-service/rental-reservation.service.types';
import { ReactComponent as BackIcon } from 'assets/icons/back.svg';
import { ReactComponent as ApproveIcon } from 'assets/icons/approve.svg';
import { ReactComponent as RejectIcon } from 'assets/icons/reject.svg';
import ApproveRentalReservationModal from '../approve-rental-reservation-modal/approve-rental-reservation-modal';
import RejectRentalReservationModal from '../reject-rental-reservation-modal/reject-rental-reservation-modal';
import styles from '../rental-portal.module.scss';

const approvalStatusTextMap: Record<RentalReservationStatusEnum, string> = {
  [RentalReservationStatusEnum.Pending]: 'Awaiting decision from property manager.',
  [RentalReservationStatusEnum.Approved]: 'Reservation approved.',
  [RentalReservationStatusEnum.Rejected]: 'Reservation rejected.',
  [RentalReservationStatusEnum.Cancelled]: 'Reservation cancelled.',
};

const formatOccupantLabel = (occupant: IRentalReservationOccupantRead): string => {
  return `${occupant.firstName} ${occupant.lastName} — ${
    ownershipTypeStatusMap[occupant.relationship as ResidentTypesEnum] ?? occupant.relationship
  }`;
};

const formatVehicleLabel = (vehicle: IRentalReservationVehicleRead): string => {
  const plate = vehicle.licensePlateState ? `${vehicle.licensePlate} (${vehicle.licensePlateState})` : vehicle.licensePlate;
  const label = `${vehicle.year} ${vehicle.make} ${vehicle.model} · ${vehicle.color} · ${plate}`;
  return vehicle.driver ? `${label} · Driver: ${vehicle.driver}` : label;
};

const formatPetLabel = (pet: IRentalReservationPetRead): string => {
  return `${pet.type} · ${pet.breed} · "${pet.name}" · ${pet.color}`;
};

interface IDetailsListSectionProps<T> {
  title: string;
  items: T[];
  emptyLabel: string;
  renderItem: (item: T) => ReactNode;
}

function DetailsListSection<T>({ title, items, emptyLabel, renderItem }: IDetailsListSectionProps<T>) {
  return (
    <Card className={`eiq-card ${styles.detailsCard} ${styles.sectionCard}`}>
      <h3 className={styles.sectionTitle}>{title}</h3>
      {items.length === 0 ? (
        <span className={styles.approvalStatusText}>{emptyLabel}</span>
      ) : (
        <ul className={styles.detailsList}>
          {items.map((item, index) => (
            <li key={index}>{renderItem(item)}</li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export const RentalReservationDetails = observer(() => {
  const { reservationId } = useParams<{ reservationId: string }>();
  const [reservation, setReservation] = useState<IRentalReservationDetailsRead>();

  const loadReservation = useCallback(() => {
    const id = reservationId ? parseInt(reservationId, 10) : undefined;
    if (id !== undefined && !isNaN(id)) {
      rentalReservationService.getRentalReservation(id).then(setReservation);
    }
  }, [reservationId]);

  useEffect(() => {
    loadReservation();
  }, [loadReservation]);

  const onReviewModalClose = (isSuccess?: boolean) => {
    if (isSuccess) {
      loadReservation();
    }
  };

  const approveModal = useModal({ onClose: onReviewModalClose });
  const rejectModal = useModal({ onClose: onReviewModalClose });

  if (!reservation) {
    return null;
  }

  const isPending = reservation.status === RentalReservationStatusEnum.Pending;
  const statusLabel = isPending ? 'Pending Review' : reservation.status;
  const nights = dayjs(reservation.leaseEndDate).diff(dayjs(reservation.leaseStartDate), 'day');
  const leasePeriodLabel = `${dayjs(reservation.leaseStartDate).format(DATE_SHORT_MMM_DD)} – ${dayjs(reservation.leaseEndDate).format(
    DATE_SHORT_MMM_DD_YYYY,
  )}`;

  return (
    <div>
      <Card className={`eiq-card eiq-card-border ${styles.detailsCard}`} style={{ width: '100%' }}>
        <Row justify="space-between" align="middle">
          <Space size={12}>
            <SH.HotkeyLink to="/eiq-manage/rental-portal/reservations" className="go-back" label={<BackIcon />} />
            <div className={styles.headerTitleBlock}>
              <span className={styles.applicationNumber}>{formatRentalReservationNumber(reservation.id)}</span>
              <h2 className="main-title">{`${formatAddressLabel(reservation.address)} — Reservation`}</h2>
              <span className={styles.submittedLine}>
                {`Submitted ${dayjs(reservation.createdAt).format(DATE_SHORT_MMM_DD_YYYY)} · ${leasePeriodLabel}`}
              </span>
            </div>
          </Space>
          <Space size={8}>
            <Tag className="eiq-tag" color={rentalReservationStatusColorMap[reservation.status]}>
              {statusLabel}
            </Tag>
            {isPending && (
              <>
                <Button
                  icon={<RejectIcon />}
                  size="middle"
                  type="primary"
                  className={styles.rejectButton}
                  onClick={rejectModal.showModal}
                  danger>
                  Reject
                </Button>
                <Button icon={<ApproveIcon />} size="middle" type="primary" onClick={approveModal.showModal}>
                  Approve
                </Button>
              </>
            )}
          </Space>
        </Row>
      </Card>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card className={`eiq-card ${styles.detailsCard} ${styles.sectionCard}`}>
            <h3 className={styles.sectionTitle}>Property</h3>
            <div className={styles.fieldsGrid}>
              <InfoField label="Address" value={formatAddressLabel(reservation.address)} />
              <InfoField label="Community" value={reservation.communityName} />
              <InfoField label="Owner" value={reservation.ownerName} />
              <InfoField
                label="Managed by"
                value={formatRentalPropertyAgencyLabel(reservation.managementType, reservation.managingAgencyName)}
              />
            </div>
          </Card>
          <Card className={`eiq-card ${styles.detailsCard} ${styles.sectionCard}`}>
            <h3 className={styles.sectionTitle}>Reservation</h3>
            <div className={styles.fieldsGrid}>
              <InfoField label="Check-in" value={dayjs(reservation.leaseStartDate).format(DATE_SHORT_MMM_DD_YYYY)} />
              <InfoField label="Check-out" value={dayjs(reservation.leaseEndDate).format(DATE_SHORT_MMM_DD_YYYY)} />
              <InfoField label="Nights" value={nights} />
            </div>
          </Card>
          <Card className={`eiq-card ${styles.detailsCard} ${styles.sectionCard}`}>
            <h3 className={styles.sectionTitle}>Primary renter</h3>
            <div className={styles.fieldsGrid}>
              <InfoField label="Name" value={`${reservation.renterFirstName} ${reservation.renterLastName}`} />
              <InfoField label="Email" value={reservation.renterEmail} />
              <InfoField label="Phone" value={reservation.renterPhone.fullNumber} />
              <InfoField label="Date of birth" value={dayjs(reservation.renterDateOfBirth).format(DATE_SHORT_MMM_DD_YYYY)} />
            </div>
          </Card>
          <DetailsListSection title="Occupants" items={reservation.occupants} emptyLabel="No occupants" renderItem={formatOccupantLabel} />
          <DetailsListSection title="Vehicles" items={reservation.vehicles} emptyLabel="No vehicles" renderItem={formatVehicleLabel} />
          <DetailsListSection title="Pets" items={reservation.pets} emptyLabel="No pets" renderItem={formatPetLabel} />
          <Card className={`eiq-card ${styles.detailsCard} ${styles.sectionCard}`}>
            <h3 className={styles.sectionTitle}>Documents</h3>
            {reservation.documents.map((document) => (
              <div key={document.fileUri} className={styles.documentRow}>
                <div className={styles.documentInfo}>
                  <span className={styles.documentName}>
                    <FileTextOutlined />
                    {document.fileName}
                  </span>
                  <span className={styles.documentType}>
                    {rentalReservationDocumentTypeMap[document.type as RentalReservationDocumentTypeEnum]}
                  </span>
                </div>
                <EIQDocumentPreview
                  url={document.fileUri}
                  fileType={UploadFileTypes.RentalPropertyDocument}
                  fileName={document.fileName}
                  contentType={document.contentType}
                />
              </div>
            ))}
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card className={`eiq-card ${styles.detailsCard} ${styles.sectionCard}`}>
            <h3 className={styles.sectionTitle}>Review priority</h3>
            <div className={styles.fieldsColumn}>
              <InfoField
                label="Requested"
                value={
                  <Tag className="eiq-tag" color={rentalReservationFeeTypeColorMap[reservation.feeType]}>
                    {`${reservation.feeType} · ${formatRentalReservationReviewPeriod(reservation.reviewBusinessDays)}`}
                  </Tag>
                }
              />
              <InfoField label="Submitted" value={dayjs(reservation.createdAt).format(DATE_SHORT_MMM_DD_YYYY)} />
              <InfoField label="Target decision" value={dayjs(reservation.targetDecisionDate).format(DATE_SHORT_MMM_DD_YYYY)} />
            </div>
          </Card>
          <Card className={`eiq-card ${styles.detailsCard} ${styles.sectionCard}`}>
            <h3 className={styles.sectionTitle}>Payment</h3>
            <div className={styles.fieldsColumn}>
              <InfoField
                label="Application fee"
                value={reservation.paymentStatus === RentalPaymentStatusEnum.Waived ? '$0.00' : `$${reservation.applicationFee.toFixed(2)}`}
              />
              <InfoField
                label="Status"
                value={
                  <Tag className="eiq-tag" color={rentalPaymentStatusColorMap[reservation.paymentStatus]}>
                    {rentalPaymentStatusMap[reservation.paymentStatus]}
                  </Tag>
                }
              />
              {reservation.externalPaymentId && (
                <InfoField
                  label="Payment ID"
                  value={
                    <Typography.Text copyable ellipsis={{ tooltip: reservation.externalPaymentId }}>
                      {reservation.externalPaymentId}
                    </Typography.Text>
                  }
                />
              )}
            </div>
          </Card>
          <Card className={`eiq-card ${styles.detailsCard} ${styles.sectionCard}`}>
            <h3 className={styles.sectionTitle}>Overlap check</h3>
            <span className={reservation.hasOverlappingReservations ? styles.overlapWarningText : styles.approvalStatusText}>
              {reservation.hasOverlappingReservations
                ? 'Overlapping reservations exist for the selected dates.'
                : 'No overlapping reservations for the selected dates.'}
            </span>
          </Card>
          <Card className={`eiq-card ${styles.detailsCard} ${styles.sectionCard}`}>
            <h3 className={styles.sectionTitle}>Approval status</h3>
            <div className={styles.fieldsColumn}>
              <span className={styles.approvalStatusText}>
                {`Last updated ${dayjs(reservation.updatedAt).format(DATE_SHORT_MMM_D_YYYY_H_MMA)}. ${
                  approvalStatusTextMap[reservation.status]
                }`}
              </span>
              {reservation.status === RentalReservationStatusEnum.Rejected && reservation.rejectionComment && (
                <InfoField
                  label="Rejection reason"
                  value={<span className={styles.rejectionComment}>{reservation.rejectionComment}</span>}
                />
              )}
            </div>
          </Card>
        </Col>
      </Row>
      <RejectRentalReservationModal
        initData={{ reservationId: reservation.id }}
        title="Reason for rejection"
        isOpen={rejectModal.isOpenModal}
        onClose={rejectModal.hideModal}
      />
      <ApproveRentalReservationModal
        initData={{ reservationId: reservation.id }}
        title="Approve rental reservation"
        isOpen={approveModal.isOpenModal}
        onClose={approveModal.hideModal}
      />
    </div>
  );
});
